import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import moment from "moment";
import { Transition } from "../../../UiComponents/Transition/Transition";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { createAttemptTracker, shouldReuseRequestId } from "../../AddMoneyToUser/addMoneyUtils";
// The Company Wallet dialogs' own look (navy header band, bordered inputs, footer), so these dialogs match them.
import Styles from "../../ClientCompanyWallet/ClientCompanyWallet.module.scss";
import {
  cancelBriskV2Certificate,
  checkBriskV2Topup,
  getBriskV2Certificate,
  listBriskV2PaymentLinks,
  listBriskV2Topups,
  resolveBriskV2Certificate,
  sendBriskV2PaymentLink,
  startBriskV2Topup,
} from "../../../Service/BriskV2Service";
import { briskCallLabel, briskCallOutcome, cancelRefundLine, historyStep, isSafeLink, parseFieldChanges, statusColor, statusLabel, walletLine } from "./briskV2ListUtils";

const when = (d) => (d ? moment(d).format("DD-MM-YYYY HH:mm") : "-");
const whenLong = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");
const amountInput = (value) => value.replace(/[^\d.]/g, "").replace(/(\.\d{0,2}).*$/, "$1");

const buttonSx = { textTransform: "none", borderRadius: "6px" };

/** The dialog frame every Brisk dialog uses: header band (title, subtitle, close), body, optional footer. */
export const DialogFrame = ({ title, subtitle, onClose, busy, maxWidth = 480, footer, children }) => (
  <Dialog
    open
    onClose={busy ? undefined : onClose}
    TransitionComponent={Transition}
    PaperProps={{ sx: { maxWidth: `${maxWidth}px !important`, width: "100%", borderRadius: "10px" } }}
  >
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between" }} className={Styles.DialogHeader}>
      <Box>
        <Typography className={Styles.DialogTitle}>{title}</Typography>
        {subtitle && <Typography className={Styles.DialogSubtitle}>{subtitle}</Typography>}
      </Box>
      <IconButton onClick={onClose} size="small" sx={{ color: "white" }} disabled={busy} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column" }} className={Styles.DialogBody}>
      {children}
    </Box>
    {footer && (
      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }} className={Styles.DialogFooter}>
        {footer}
      </Box>
    )}
  </Dialog>
);

/** A labelled, bordered input (the Company Wallet dialogs' field). */
const InputField = ({ id, label, required, hint, error, mt = 0, ...inputProps }) => (
  <Box sx={{ display: "flex", flexDirection: "column", mt }}>
    <Typography component="label" htmlFor={id} className={Styles.FieldLabel}>
      {label}
      {required && <span style={{ color: "#B42318" }}> *</span>}
    </Typography>
    <input id={id} className={Styles.InputFiled} {...inputProps} />
    {error ? (
      <Typography className={Styles.FieldError}>{error}</Typography>
    ) : (
      hint && <Typography sx={{ fontSize: 11, color: "#667085", mt: "3px" }}>{hint}</Typography>
    )}
  </Box>
);

/** A small bordered table with a light header row, and a line when there is nothing to show. */
const SmallTable = ({ columns, rows, empty, renderRow }) => (
  <Box sx={{ border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden", mt: 2 }}>
    <Table size="small" sx={{ "& th": { backgroundColor: "#F5F7FB", fontWeight: 600, fontSize: 12, color: "#344054" }, "& td": { fontSize: 12.5, color: "#101828" } }}>
      <TableHead>
        <TableRow>
          {columns.map((c) => (
            <TableCell key={c}>{c}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length ? (
          rows.map(renderRow)
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ textAlign: "center", color: "#667085 !important", py: 2 }}>
              {empty}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Box>
);

/** Cancel at Brisk (admin only). Says what comes back to the wallet before it is pressed. */
export const CancelCertificateDialog = ({ row, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const submit = async () => {
    setBusy(true);
    let res;
    try {
      res = await cancelBriskV2Certificate(row.id, reason);
    } catch (error) {
      ToastError(error.message || "Could not cancel this certificate");
      return;
    } finally {
      setBusy(false);
    }
    if (res.error) {
      ToastError(res.message);
      return;
    }
    ToastSuccess(res.message);
    onDone();
  };
  return (
    <DialogFrame
      title="Cancel Certificate"
      subtitle={`${row.certificateNo} · ${row.customerName}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button variant="outlined" className={Styles.RemoveButton} onClick={submit} disabled={busy || reason.trim().length < 3} startIcon={busy ? <CircularProgress size={16} /> : null}>
            {busy ? "Cancelling…" : "Cancel Certificate"}
          </Button>
        </>
      }
    >
      <Box className={Styles.PanelBalanceRow}>
        {cancelRefundLine(row)}
      </Box>
      <InputField
        id="brisk-cancel-reason"
        label="Reason"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={200}
        placeholder="Why is it cancelled?"
        hint="3 to 200 letters, sent to Brisk"
      />
    </DialogFrame>
  );
};

/** An admin settles a certificate the system could not: Brisk made it (number), it was not made, or settled by hand. */
export const ResolveDialog = ({ row, onClose, onDone }) => {
  const refundStuck = row && row.refund && ["MISMATCH", "FAILED"].includes(row.refund.state);
  const [action, setAction] = useState(refundStuck ? "SETTLED_BY_HAND" : "CREATED");
  const [certificateNo, setCertificateNo] = useState("");
  const [confirmChassisNo, setConfirmChassisNo] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const submit = async () => {
    setBusy(true);
    let res;
    try {
      res = await resolveBriskV2Certificate(row.id, { action, certificateNo, confirmChassisNo, remark });
    } catch (error) {
      ToastError(error.message || "Could not save this resolution");
      return;
    } finally {
      setBusy(false);
    }
    if (res.error) {
      ToastError(res.message);
      return;
    }
    ToastSuccess(res.message);
    onDone();
  };
  return (
    <DialogFrame
      title="Resolve"
      subtitle={`${row.customerName} · chassis ${row.chassisNo}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button variant="contained" disableElevation className={Styles.TopupButton} onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : null}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <RadioGroup value={action} onChange={(e) => setAction(e.target.value)} sx={{ mb: 1 }}>
        {!refundStuck && <FormControlLabel value="CREATED" control={<Radio size="small" />} label="Brisk made it (type the number from Brisk)" />}
        {!refundStuck && <FormControlLabel value="NOT_CREATED" control={<Radio size="small" />} label="Brisk did not make it (refund)" />}
        {refundStuck && <FormControlLabel value="SETTLED_BY_HAND" control={<Radio size="small" />} label="The refund was settled by hand" />}
      </RadioGroup>
      {action === "CREATED" && (
        <InputField id="brisk-resolve-number" label="Certificate number" required value={certificateNo} onChange={(e) => setCertificateNo(e.target.value)} placeholder="e.g. TW2610170000001" />
      )}
      {action === "NOT_CREATED" && (
        <InputField
          id="brisk-resolve-chassis"
          label={`Type the chassis number (${row.chassisNo}) to confirm`}
          required
          value={confirmChassisNo}
          onChange={(e) => setConfirmChassisNo(e.target.value)}
        />
      )}
      {action === "SETTLED_BY_HAND" && (
        <InputField id="brisk-resolve-remark" label="What was done" required value={remark} onChange={(e) => setRemark(e.target.value)} hint="No money moves here" />
      )}
    </DialogFrame>
  );
};

const NAVY = "#1E3A5F";
const STEP_COLOURS = { navy: NAVY, green: "#067647", red: "#B42318", amber: "#B54708", grey: "#667085" };
const STEP_ICONS = {
  sent: SendRoundedIcon,
  done: CheckRoundedIcon,
  failed: ClearRoundedIcon,
  cancel: BlockRoundedIcon,
  warning: PriorityHighRoundedIcon,
  money: CurrencyRupeeRoundedIcon,
  edit: EditRoundedIcon,
  note: NotesRoundedIcon,
  recheck: RefreshRoundedIcon,
};
const sectionTitleSx = { fontSize: 13.5, fontWeight: 700, color: NAVY, mt: 2.5, mb: 1 };

/** The certificate at a glance: where it stands, who paid, and its details. A value it does not have is left out. */
const HistorySummary = ({ record }) => {
  const rupees = (v) => (v ? `₹${v}` : "");
  const refund = record.refund && record.refund.state === "DONE" && record.refund.amount ? `₹${record.refund.amount}` : "";
  const fields = [
    ["Customer", record.customerName],
    ["Mobile", record.mobileNo],
    ["Policy number", record.certificateNo],
    ["Reference number", record.source && record.source.policyNo],
    ["Plan", record.planName],
    ["Policy type", record.policyType],
    ["Total premium", rupees(record.totalPremium)],
    ["Plan price", rupees(record.planPrice)],
    ["Net", rupees(record.netPremium)],
    ["GST", rupees(record.gst)],
    ["Refunded", refund],
    ["Engine number", record.engineNo],
    ["Chassis number", record.chassisNo],
    ["Registration number", record.registrationNo],
    ["Created", whenLong(record.createdAt)],
    ["Last updated", whenLong(record.updatedAt)],
  ].filter(([, value]) => value);
  const colour = statusColor(record.status);
  return (
    <Box data-testid="brisk-history-summary" sx={{ border: "1px solid #DCE4EE", borderRadius: "10px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: 1.5, py: 1, backgroundColor: "#EEF3F9", borderBottom: "1px solid #DCE4EE" }}>
        <Box
          component="span"
          data-testid="brisk-history-status"
          sx={{ px: 1.25, py: 0.25, borderRadius: "999px", fontSize: 12.5, fontWeight: 700, color: "#fff", backgroundColor: colour }}
        >
          {statusLabel(record.status)}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054" }}>{walletLine(record)}</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(170px, 100%), 1fr))", columnGap: 1.5, rowGap: 1, p: 1.5 }}>
        {fields.map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography component="div" sx={{ fontSize: 11.5, lineHeight: "16px", color: "#667085" }}>
              {label}
            </Typography>
            <Typography component="div" sx={{ fontSize: 13, lineHeight: "19px", fontWeight: 600, color: "#101828", overflowWrap: "anywhere" }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** One changed value: its name, the old value struck through, the new one. */
const FieldChanges = ({ lead, changes }) => (
  <Box sx={{ mt: 0.25 }}>
    <Typography component="div" sx={{ fontSize: 12.5, color: "#475467" }}>
      {lead}
    </Typography>
    <Box sx={{ mt: 0.5, display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.25 }}>
      {changes.map((c) => (
        <React.Fragment key={c.field}>
          <Typography component="div" sx={{ fontSize: 12, color: "#667085" }}>
            {c.label}
          </Typography>
          <Typography component="div" sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>
            <Box component="span" sx={{ color: "#98A2B3", textDecoration: c.from === "-" ? "none" : "line-through" }}>
              {c.from}
            </Box>
            <Box component="span" sx={{ mx: 0.75, color: "#98A2B3" }}>
              →
            </Box>
            <Box component="span" sx={{ fontWeight: 600, color: "#101828" }}>
              {c.to}
            </Box>
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

/** What happened, oldest first: a circle in the step's colour, a line to the next step, a short title and the full sentence. */
const HistoryTimeline = ({ history }) => {
  if (!history.length) return <Typography sx={{ fontSize: 12.5, color: "#667085" }}>Nothing recorded yet</Typography>;
  return (
    <Box data-testid="brisk-history-timeline">
      {history.map((entry, i) => {
        const step = historyStep(entry);
        const Icon = STEP_ICONS[step.icon] || NotesRoundedIcon;
        const parsed = entry.kind === "fields" ? parseFieldChanges(entry.text) : null;
        return (
          <Box
            key={i}
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "26px minmax(0, 1fr)",
              columnGap: 1.25,
              pb: 1.5,
              "&:last-of-type": { pb: 0 },
              // the line down to the next step
              "&:not(:last-of-type)::after": { content: '""', position: "absolute", left: "12px", top: "28px", bottom: "2px", width: "2px", backgroundColor: "#E4E7EC" },
            }}
          >
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: STEP_COLOURS[step.tone], color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1.5, minHeight: 26 }}>
                <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 700, color: STEP_COLOURS[step.tone] }}>
                  {step.title}
                </Typography>
                <Typography component="div" sx={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>
                  {whenLong(entry.at)}
                </Typography>
              </Box>
              {parsed ? (
                <FieldChanges lead={parsed.lead} changes={parsed.changes} />
              ) : (
                <Typography component="div" sx={{ fontSize: 12.5, color: "#475467", overflowWrap: "anywhere" }}>
                  {step.icon === "recheck" ? entry.text.replace(/^Recheck\.\s*/, "") : entry.text}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/** Everything that happened to one certificate: where it stands, its details, the story in plain words, then Brisk's calls for support. */
export const HistoryDialog = ({ row, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    setLoading(true);
    getBriskV2Certificate(row.id)
      .then((res) => {
        if (!alive) return;
        if (res.error) setError(res.message);
        else setDetail(res.data);
      })
      .catch((e) => {
        if (alive) setError(e.message || "Could not load certificate history");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [row]);
  if (!row) return null;
  return (
    <DialogFrame
      title="History"
      subtitle={`${row.certificateNo || row.customerName} · ${row.apiEnv}`}
      onClose={onClose}
      maxWidth={960}
      footer={
        <Button variant="outlined" sx={buttonSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* the row is on screen at once; the full record fills it in when it arrives */}
      <HistorySummary record={{ ...row, ...(detail || {}) }} />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Typography sx={sectionTitleSx}>What happened</Typography>
      {loading && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} aria-label="Loading certificate history" />}
      {detail && <HistoryTimeline history={detail.history || []} />}
      {detail && <BriskCallsList calls={detail.briskCalls || []} />}
    </DialogFrame>
  );
};

const pretty = (value) => (typeof value === "string" ? value : JSON.stringify(value === undefined ? null : value, null, 2));
const codeBoxSx = {
  m: 0,
  p: 1.25,
  backgroundColor: "#F5F7FB",
  border: "1px solid #EAECF0",
  borderRadius: "6px",
  fontSize: 12,
  fontFamily: "Menlo, Consolas, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 260,
  overflow: "auto",
};

const MONO = "Menlo, Consolas, monospace";

/**
 * The address one saved call went to (shared with the RoadZen list): method, full URL, HTTP status and time, then the base
 * URL in use at that moment. Calls saved before 2026-11-04 kept only `fallback` (Brisk's endpoint name, RoadZen's path).
 */
export const CallRequestUrl = ({ call: c, fallback }) => (
  <>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 0.5 }}>Request URL</Typography>
    <Box sx={{ px: 1.25, py: 1, mb: c.url ? 1.5 : 0.5, backgroundColor: "#F5F7FB", border: "1px solid #EAECF0", borderRadius: "6px" }}>
      <Box data-testid="call-url" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        {c.method && (
          <Box component="span" sx={{ px: 0.75, borderRadius: "4px", fontSize: 11, fontWeight: 700, lineHeight: "18px", color: NAVY, backgroundColor: "#E3EAF3", fontFamily: MONO }}>
            {c.method}
          </Box>
        )}
        <Typography component="span" sx={{ flex: "1 1 240px", minWidth: 0, fontSize: 12, fontFamily: MONO, color: "#101828", overflowWrap: "anywhere" }}>
          {c.url || fallback}
        </Typography>
        <Typography component="span" sx={{ fontSize: 12, color: "#475467", whiteSpace: "nowrap" }}>
          {[c.sent === "ANSWERED" ? `HTTP ${c.httpStatus}` : "", typeof c.ms === "number" ? `${(c.ms / 1000).toFixed(1)} s` : ""].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      {c.baseUrl && (
        <Box data-testid="call-base-url" sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, mt: 0.75, pt: 0.75, borderTop: "1px dashed #E4E7EC" }}>
          <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: "#667085", whiteSpace: "nowrap" }}>
            Base URL used
          </Typography>
          <Typography component="span" sx={{ flex: "1 1 240px", minWidth: 0, fontSize: 11.5, fontFamily: MONO, color: "#475467", overflowWrap: "anywhere" }}>
            {c.baseUrl}
          </Typography>
        </Box>
      )}
    </Box>
    {!c.url && <Typography sx={{ fontSize: 11.5, color: "#667085", mb: 1.5 }}>An older call: the full address was not saved.</Typography>}
  </>
);

/**
 * Every call made to Brisk for this certificate, folded away: the story above is what an operator reads, and this is for
 * support. Each call says what it was for in plain words; opening it shows the exact call, what was sent (dealer login
 * masked) and what Brisk answered.
 */
export const BriskCallsList = ({ calls }) => (
  <Accordion disableGutters elevation={0} data-testid="brisk-calls" sx={{ mt: 2.5, border: "1px solid #EAECF0", borderRadius: "8px !important", "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1, flexWrap: "wrap" } }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: NAVY }}>Details for support</Typography>
      <Typography sx={{ fontSize: 12, color: "#667085" }}>
        {calls.length === 1 ? "1 call to Brisk" : `${calls.length} calls to Brisk`}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      {!calls.length && <Typography sx={{ fontSize: 12.5, color: "#667085" }}>No call to Brisk saved for this certificate</Typography>}
      {calls.map((c, i) => {
        const outcome = briskCallOutcome(c);
        return (
          <Accordion key={i} disableGutters elevation={0} sx={{ border: "1px solid #EAECF0", borderRadius: "8px !important", mb: 1, "&:last-of-type": { mb: 0 }, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, "& .MuiAccordionSummary-content": { my: 0.5, alignItems: "center", gap: 1, flexWrap: "wrap" } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#101828" }}>{briskCallLabel(c.endpoint)}</Typography>
              <Chip size="small" label={outcome.label} color={outcome.ok ? "success" : "warning"} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              <Typography sx={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>{moment(c.at).format("DD MMM YYYY, hh:mm:ss A")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <CallRequestUrl call={c} fallback={c.endpoint} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 0.5 }}>What we sent</Typography>
                  <Box component="pre" sx={codeBoxSx}>
                    {pretty(c.request)}
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 0.5 }}>
                    What Brisk answered{c.errorCode ? ` (${c.errorCode})` : ""}
                  </Typography>
                  <Box component="pre" sx={codeBoxSx}>
                    {pretty(c.response)}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </AccordionDetails>
  </Accordion>
);

/** Top up Brisk (admin): starts a Brisk order, opens its payment page, and checks whether it was paid. */
export const BriskTopupDialog = ({ open, onClose, onCredited }) => {
  const [amount, setAmount] = useState("");
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const tracker = useRef(createAttemptTracker());
  const load = async () => {
    setLoadingOrders(true);
    try {
      const res = await listBriskV2Topups();
      if (res.error) ToastError(res.message);
      else setOrders(res.data || []);
    } catch (error) {
      ToastError(error.message || "Could not load Brisk top-ups");
    } finally {
      setLoadingOrders(false);
    }
  };
  useEffect(() => {
    if (open) load();
  }, [open]);
  if (!open) return null;
  const start = async () => {
    setBusy(true);
    let res;
    try {
      res = await startBriskV2Topup({ amount, requestId: tracker.current.idFor({ userId: "brisk-topup", amount }) });
    } catch (error) {
      ToastError(error.message || "Could not start the Brisk top-up");
      return;
    } finally {
      setBusy(false);
    }
    if (!shouldReuseRequestId(res)) tracker.current.reset();
    if (res.error) {
      ToastError(res.message);
      return;
    }
    if (res.data.checkoutUrl && isSafeLink(res.data.checkoutUrl)) window.open(res.data.checkoutUrl, "_blank", "noopener,noreferrer");
    ToastSuccess(res.message);
    setAmount("");
    load();
  };
  const check = async (order) => {
    setBusy(true);
    let res;
    try {
      res = await checkBriskV2Topup(order.id);
    } catch (error) {
      ToastError(error.message || "Could not check the top-up status");
      return;
    } finally {
      setBusy(false);
    }
    if (res.error) ToastError(res.message);
    else {
      ToastSuccess(res.message);
      if (res.data && res.data.status === "CREDITED") onCredited();
    }
    load();
  };
  return (
    <DialogFrame
      title="Top up Brisk"
      subtitle="Pay Brisk online · added to the Brisk wallet once Brisk confirms"
      onClose={onClose}
      busy={busy}
      maxWidth={760}
      footer={
        <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
          Close
        </Button>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        When Brisk confirms the payment, the paid amount is added to the Brisk wallet once. TEST payments are not added.
      </Alert>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "flex-end" }, gap: "12px" }}>
        <Box sx={{ flex: 1 }}>
          <InputField
            id="brisk-topup-amount"
            label="Amount (₹)"
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(amountInput(e.target.value))}
            placeholder="Enter amount"
          />
        </Box>
        <Button variant="contained" disableElevation className={Styles.TopupButton} sx={{ height: 36 }} onClick={start} disabled={busy || !amount} startIcon={busy ? <CircularProgress size={16} /> : null}>
          {busy ? "Starting…" : "Pay Brisk"}
        </Button>
      </Box>
      {loadingOrders && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} aria-label="Loading Brisk top-ups" />}
      {!loadingOrders && (
        <SmallTable
          columns={["Date", "Amount", "Order", "Status", ""]}
          rows={orders}
          empty="No top-ups yet"
          renderRow={(o) => (
            <TableRow key={o.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{when(o.createdAt)}</TableCell>
              <TableCell>₹{o.amount}</TableCell>
              <TableCell>
                {o.orderId || "-"}
                {o.checkoutUrlLooksLocal && (
                  <Typography variant="caption" display="block" color="error">
                    Brisk's payment page points at a test machine
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {o.status}
                {o.credit && o.credit.mismatch ? ` (paid differs by ₹${o.credit.mismatch})` : ""}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {o.orderId && o.status !== "CREDITED" && (
                  <Button size="small" sx={buttonSx} onClick={() => check(o)} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : null}>
                    {busy ? "Checking…" : "Check status"}
                  </Button>
                )}
                {o.checkoutUrl && o.status === "AWAITING_PAYMENT" && isSafeLink(o.checkoutUrl) && (
                  <Button size="small" sx={buttonSx} component="a" href={o.checkoutUrl} target="_blank" rel="noopener noreferrer">
                    Pay
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )}
        />
      )}
    </DialogFrame>
  );
};

/** Send Payment Link (admin): Brisk emails and texts a top-up link. A record only — nothing is added to a wallet. */
export const PaymentLinkDialog = ({ open, onClose }) => {
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const tracker = useRef(createAttemptTracker());
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadingLinks(true);
    listBriskV2PaymentLinks()
      .then((res) => {
        if (!alive) return;
        if (res.error) ToastError(res.message);
        else setLinks(res.data || []);
      })
      .catch((error) => {
        if (alive) ToastError(error.message || "Could not load payment links");
      })
      .finally(() => {
        if (alive) setLoadingLinks(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);
  if (!open) return null;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const send = async () => {
    setBusy(true);
    let res;
    try {
      res = await sendBriskV2PaymentLink({
        amount,
        email,
        mobile,
        requestId: tracker.current.idFor({ userId: `${email}|${mobile}`, amount }),
      });
    } catch (error) {
      ToastError(error.message || "Could not send the payment link");
      return;
    } finally {
      setBusy(false);
    }
    if (!shouldReuseRequestId(res)) tracker.current.reset();
    if (res.error) {
      ToastError(res.message);
      return;
    }
    ToastSuccess(res.message);
    setAmount("");
    setLoadingLinks(true);
    listBriskV2PaymentLinks()
      .then((r) => {
        if (r.error) ToastError(r.message);
        else setLinks(r.data || []);
      })
      .catch((error) => ToastError(error.message || "Could not refresh payment links"))
      .finally(() => setLoadingLinks(false));
  };
  return (
    <DialogFrame
      title="Send Payment Link"
      subtitle="Brisk sends a top-up link by email and SMS"
      onClose={onClose}
      busy={busy}
      maxWidth={760}
      footer={
        <>
          <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="contained"
            disableElevation
            className={Styles.TopupButton}
            onClick={send}
            disabled={busy || !amount || !emailOk || mobile.length !== 10}
            startIcon={busy ? <CircularProgress size={16} /> : null}
          >
            {busy ? "Sending…" : "Send Link"}
          </Button>
        </>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Money paid through this link is not added to the Brisk wallet automatically. Add it on the Company Wallet page when it arrives.
      </Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr 1fr" }, gap: "12px" }}>
        <InputField
          id="brisk-link-amount"
          label="Amount (₹)"
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(amountInput(e.target.value))}
          placeholder="Enter amount"
        />
        <InputField
          id="brisk-link-email"
          label="Email"
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value.trim())}
          placeholder="customer@example.com"
          error={email && !emailOk ? "Invalid email format" : ""}
        />
        <InputField
          id="brisk-link-mobile"
          label="Mobile"
          required
          inputMode="numeric"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder="10 digits"
          error={mobile && mobile.length !== 10 ? "Mobile number must be 10 digits" : ""}
        />
      </Box>
      {loadingLinks && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} aria-label="Loading payment links" />}
      {!loadingLinks && (
        <SmallTable
          columns={["Date", "Amount", "Sent to", "Status"]}
          rows={links}
          empty="No links sent yet"
          renderRow={(l) => (
            <TableRow key={l.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{when(l.createdAt)}</TableCell>
              <TableCell>₹{l.amount}</TableCell>
              <TableCell>
                {l.email} · {l.mobile}
              </TableCell>
              <TableCell>{l.status}</TableCell>
            </TableRow>
          )}
        />
      )}
    </DialogFrame>
  );
};
